import { colorStopToString, fixTransparentColorStops } from '../color-utils.js';

/**
 * Render a conic gradient as a CanvasPattern.
 * We normalize CSS angles (0deg at top, clockwise) to Canvas' conic
 * gradient angles (0 radians at 3 o'clock, clockwise) and paint into
 * a temporary canvas, then create a non-repeating pattern from it.
 *
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {Object} gradientDef - Gradient definition from parser
 * @param {Object} bounds - Bounding box { x, y, w, h }
 * @returns {CanvasPattern} Canvas pattern that can be used as fillStyle
 */
export function renderConicGradient(context, gradientDef, { x, y, w, h }) {
  // Parse position from orientation
  const position = gradientDef.orientation?.position;
  let centerX = w * 0.5; // Default to center
  let centerY = h * 0.5;

  if (position) {
    // Handle percentage values for x and y
    if (position.x?.type === '%') {
      centerX = w * (parseFloat(position.x.value) / 100);
    }
    if (position.y?.type === '%') {
      centerY = h * (parseFloat(position.y.value) / 100);
    }
  }

  // Parse angle
  // CSS conic-gradient: 0deg points up, goes clockwise
  // Canvas createConicGradient: 0 radians points right (3 o'clock), goes clockwise
  // We convert CSS angle to radians and offset by -PI/2 so that 0deg maps to the top.
  let angle = 0;
  if (gradientDef.orientation?.value) {
    angle = parseFloat(gradientDef.orientation.value);
    // Normalize angle to be between 0 and 360
    angle = ((angle % 360) + 360) % 360;
    // Convert to radians and adjust 0° to point upward
    angle = (angle * Math.PI) / 180 - Math.PI / 2;
  } else {
    // Default: start from top (12 o'clock position)
    angle = -Math.PI / 2;
  }

  // Create a temporary canvas for the gradient
  const tempCanvas = new OffscreenCanvas(w, h);
  const tempCtx = tempCanvas.getContext('2d');

  // Create gradient centered at the requested position
  const gradient = tempCtx.createConicGradient(angle, centerX, centerY);

  // Clone colorStops to avoid mutating the original
  const colorStops = gradientDef.colorStops.map((stop) => ({ ...stop }));

  // Fix transparent color stops to match CSS premultiplied alpha behavior
  fixTransparentColorStops(colorStops);

  // Add color stops
  let lastPosition = 0;
  let lastColorValue = null;

  colorStops.forEach((stop) => {
    let stopPosition = 0;
    if (stop.length?.type === '%') {
      stopPosition = Math.max(0, Math.min(1, parseFloat(stop.length.value) / 100));
    } else if (stop.length?.type === 'deg') {
      // Convert degrees to 0-1 range (360deg = 1)
      stopPosition = Math.max(0, Math.min(1, parseFloat(stop.length.value) / 360));
    }

    const colorValue = colorStopToString(stop);

    if (colorValue) {
      gradient.addColorStop(stopPosition, colorValue);
      lastPosition = stopPosition;
      lastColorValue = colorValue;
    }
  });

  // If the last color stop is not at 100%, extend it to 100%
  // This matches CSS behavior where the last color continues to the edge
  if (lastPosition < 1 && lastColorValue) {
    gradient.addColorStop(1, lastColorValue);
  }

  // Fill the temp canvas with the gradient
  tempCtx.fillStyle = gradient;
  tempCtx.fillRect(0, 0, w, h);

  // Create a pattern from the temp canvas
  const pattern = context.createPattern(tempCanvas, 'no-repeat');

  // Set the pattern transform to position it correctly
  pattern.setTransform(new DOMMatrix().translate(x, y));

  return pattern;
}
